import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { Loader2 } from "lucide-react";
import { isChunkLoadError, tryReloadOnce } from "@/lib/chunkErrorRecovery";
import { useT } from "@/lib/translation";

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
  fallbackRender?: (props: { resetError: () => void }) => ReactNode;
}

interface State {
  hasError: boolean;
  isReloading: boolean;
}

// Class components cannot use hooks, so the default fallback text lives in this small
// function component.
const DefaultErrorFallback = () => {
  const t = useT();
  return (
    <div className="p-4 border border-destructive/50 bg-destructive/10 rounded-lg text-sm text-destructive">
      {t("common:errorBoundary.componentError")}
    </div>
  );
};

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    isReloading: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    if (isChunkLoadError(error?.message, error?.name)) {
      tryReloadOnce();
      // A reload is either just triggered above, or already in flight from a
      // previous render of this same failure a moment ago (window.location.reload()
      // is not synchronous, so React can retry rendering the broken chunk before the
      // navigation actually lands). Either way, show a neutral loading state instead
      // of the error fallback — the page is about to correct itself.
      return { hasError: false, isReloading: true };
    }

    return { hasError: true, isReloading: false };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error?.message, error?.name)) {
      // Reload is handled by getDerivedStateFromError (or already in flight from a
      // moment ago) — never report this known, self-healing, deploy-related error.
      return;
    }

    console.error("Uncaught error:", error, errorInfo);
    // TECH-DEBT: double cast via unknown — ErrorInfo has componentStack: string | null;
    // could pass { componentStack: errorInfo.componentStack } directly.
    Sentry.captureException(error, {
      extra: errorInfo as unknown as Record<string, unknown>,
    });
    posthog.capture("error_encountered", {
      message: error.message,
      name: error.name,
      componentStack: errorInfo.componentStack,
    });
  }

  private resetError = () => {
    this.setState({ hasError: false });
  };

  public render() {
    if (this.state.isReloading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }

    if (this.state.hasError) {
      if (this.props.fallbackRender) {
        return this.props.fallbackRender({ resetError: this.resetError });
      }
      return this.props.fallback || <DefaultErrorFallback />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
